import { useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import type { InspectionTemplate } from 'inspection-types';
import { useForkInspectionTemplate } from 'react-hooks';
import { SourceType } from 'graphql-query-contracts';

import { isTemplateOwnedByChannel } from '../helpers/isTemplateOwnedByChannel';
import { TEMPLATE_LIBRARY_PATH } from '../navigation/useInspectionsNavigation';
import type { InspectionsChannel } from '../types/channel';

type UseCustomizeInspectionTemplateArgs = {
  channel: InspectionsChannel;
  channelBasePath: string;
};

type UseCustomizeInspectionTemplateResult = {
  customizeTemplate: (template: InspectionTemplate) => Promise<void>;
};

/**
 * Orchestrates the "Customize" action on the inspections template library.
 *
 * Flow:
 *  - If the channel already owns the template, navigate directly to its edit page.
 *  - Otherwise, show a loading modal, fork the template for the current channel,
 *    then navigate to the newly forked template's edit page.
 *  - On error, hide the modal and surface a toast; no navigation occurs.
 *  - Concurrent calls are de-duped via an in-flight guard.
 */
export function useCustomizeInspectionTemplate({
  channel,
  channelBasePath,
}: UseCustomizeInspectionTemplateArgs): UseCustomizeInspectionTemplateResult {
  const { t } = useTranslation();
  const history = useHistory();
  const { forkInspectionTemplate } = useForkInspectionTemplate();
  const inFlightRef = useRef(false);

  const customizeTemplate = useCallback(
    async (template: InspectionTemplate) => {
      if (inFlightRef.current) {
        return;
      }

      if (isTemplateOwnedByChannel(template, channel)) {
        history.push(
          `${channelBasePath}/${TEMPLATE_LIBRARY_PATH}/${template.id}/edit`
        );

        return;
      }

      inFlightRef.current = true;

      try {
        window.Alert.loading({
          title: t('web.admin.inspections.templateLibrary.customize.preparing'),
          disableCloseOnBackgroundClick: true,
        });

        const buildingEntityType = 'building';

        const { templateId } = await forkInspectionTemplate({
          parentTemplateId: template.id,
          buildingId: {
            entityId: channel._id,
            entityType: buildingEntityType,
            sourceType: SourceType.SourceTypeActivate,
          },
        });

        window.Alert.hide();
        history.push(
          `${channelBasePath}/${TEMPLATE_LIBRARY_PATH}/${templateId}/edit`
        );
      } catch {
        window.Alert.hide();
        await window.Toast.show(
          t('web.admin.inspections.templateLibrary.customize.errorToast')
        );
      } finally {
        inFlightRef.current = false;
      }
    },
    [channel, channelBasePath, forkInspectionTemplate, history, t]
  );

  return { customizeTemplate };
}
